/**
 * useAudioPlayer — global player state plus queue / history / playlist
 * surfaces. Backward-compatible: every existing caller keeps using
 * `play`, `pause`, `toggle`, `stop`, `seek` and the same state refs.
 *
 * Added in this iteration:
 *  - `queue` / `history` refs for upcoming + recently-played tracks
 *  - `playlists` ref + CRUD wrappers around /api/shop/playlists/*
 *  - `playNext`, `addToQueue`, `removeFromQueue`, `clearQueue`
 *  - `advanceQueue()` — used by MasterPlayer.vue on `ended`
 *  - `drawerOpen` / `drawerTab` for the slide-in panel
 *
 * Queue + drawer state are persisted to `localStorage` so reloading
 * the shop doesn't lose what's stacked up. History is server-backed
 * (best effort) — anonymous users get a client-only timeline.
 */
const QUEUE_STORAGE_KEY = 'audio-player.queue.v1'

export const useAudioPlayer = () => {
    const currentTrack = useState('audio-player-track', () => null) // { id, url, peaksUrl, title, duration, type, imageUrl }
    const isPlaying = useState('audio-player-playing', () => false)
    const currentTime = useState('audio-player-time', () => 0)
    const duration = useState('audio-player-duration', () => 0)
    const volume = useState('audio-player-volume', () => 1)

    // ── New state ──────────────────────────────────────────────
    const queue = useState('audio-player-queue', () => [])
    const history = useState('audio-player-history', () => [])
    const playlists = useState('audio-player-playlists', () => [])
    const drawerOpen = useState('audio-player-drawer-open', () => false)
    const drawerTab = useState('audio-player-drawer-tab', () => 'queue') // 'queue' | 'history' | 'playlists'
    // Pixel coordinates the drawer's spawn animation should originate
    // from. The MasterPlayer's queue button writes its bounding-rect
    // centre here on every open, so the clip-path / particle burst can
    // explode outward from EXACTLY the icon the user clicked instead
    // of an abstract corner.
    const drawerOrigin = useState('audio-player-drawer-origin', () => ({ x: 0, y: 0 }))
    const queueLoaded = useState('audio-player-queue-loaded', () => false)
    // Bumped whenever we push into the history list — the drawer's
    // "History" tab listens for this so the pill animation can play
    // when a new track lands.
    const historyPulseAt = useState('audio-player-history-pulse', () => 0)

    // Best-effort localStorage hydration. Runs once per browser tab,
    // guarded by SSR + queueLoaded so the watcher doesn't fight the
    // initial restore. Errors (quota, private-mode) are swallowed.
    const hydrateQueue = () => {
        if (queueLoaded.value) return
        if (typeof window === 'undefined') return
        try {
            const raw = window.localStorage.getItem(QUEUE_STORAGE_KEY)
            if (raw) {
                const parsed = JSON.parse(raw)
                if (Array.isArray(parsed)) queue.value = parsed
            }
        } catch {/* ignore */}
        queueLoaded.value = true
    }
    const persistQueue = () => {
        if (typeof window === 'undefined') return
        try {
            window.localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue.value))
        } catch {/* ignore */}
    }

    // Actions — unchanged signatures
    const play = (track, options = {}) => {
        const { startTime, source } = options || {}
        if (currentTrack.value && currentTrack.value.id === track.id) {
            isPlaying.value = true
            if (startTime !== undefined) seek(startTime)
            return
        }
        currentTrack.value = track
        isPlaying.value = true
        currentTime.value = startTime !== undefined ? startTime : 0
        // Best-effort: log to server-side history. Anonymous → client-only.
        logHistory(track, source).catch(() => {/* ignore */})
    }

    const pause = () => { isPlaying.value = false }

    const toggle = (track) => {
        if (currentTrack.value && currentTrack.value.id === track.id) {
            isPlaying.value = !isPlaying.value
        } else {
            play(track)
        }
    }

    const stop = () => {
        isPlaying.value = false
        currentTime.value = 0
    }

    const seek = (time) => { currentTime.value = time }

    // ── Queue ──────────────────────────────────────────────────
    const addToQueue = (track) => {
        hydrateQueue()
        queue.value = [...queue.value, track]
        persistQueue()
    }
    const playNext = (track) => {
        hydrateQueue()
        queue.value = [track, ...queue.value]
        persistQueue()
    }
    const removeFromQueue = (index) => {
        hydrateQueue()
        if (index < 0 || index >= queue.value.length) return
        const next = queue.value.slice()
        next.splice(index, 1)
        queue.value = next
        persistQueue()
    }
    const clearQueue = () => {
        queue.value = []
        persistQueue()
    }
    const moveInQueue = (from, to) => {
        if (from === to) return
        if (from < 0 || from >= queue.value.length) return
        if (to < 0 || to >= queue.value.length) return
        const next = queue.value.slice()
        const [moved] = next.splice(from, 1)
        next.splice(to, 0, moved)
        queue.value = next
        persistQueue()
    }
    // Pop the next queued track and play it. Returns true if there was
    // something to play. Called by MasterPlayer.vue when the audio
    // `ended` event fires.
    const advanceQueue = () => {
        hydrateQueue()
        if (queue.value.length === 0) return false
        const [next, ...rest] = queue.value
        queue.value = rest
        persistQueue()
        play(next, { source: 'queue' })
        return true
    }

    // ── History ────────────────────────────────────────────────
    const logHistory = async (track, source) => {
        // Push the local cache immediately so the drawer reflects the
        // play before the round trip lands. Cap at 200 to keep the
        // client-side list bounded.
        const localEntry = {
            _id: `local_${Date.now()}_${track.id || ''}`,
            track,
            playedAt: new Date().toISOString(),
            source: source || 'unknown'
        }
        history.value = [localEntry, ...history.value].slice(0, 200)
        historyPulseAt.value = Date.now()
        // Server-side log — best effort. The endpoint 401s anonymous.
        try {
            await $fetch('/api/shop/history', {
                method: 'POST',
                body: { trackId: track.id || track._id, source: source || 'unknown' }
            })
        } catch {/* ignore — anonymous or offline */}
    }
    const fetchHistory = async () => {
        try {
            const res = await $fetch('/api/shop/history', { params: { limit: 60 } })
            history.value = (res?.items || []).map(serverHistoryToTrack)
        } catch {/* ignore */}
    }

    /**
     * Wipe the history — the server rows and the local list.
     *
     * A 401 is a SUCCESS here, not a failure. Anonymous listeners still build
     * up a history: logHistory always pushes into the local list and only
     * treats the server POST as best-effort, precisely because that endpoint
     * 401s them. So for a signed-out visitor there is nothing on the server to
     * delete, nothing that can come back on the next fetch, and clearing the
     * local list IS the whole operation. Treating it as an error is what made
     * "Clear history" report a failure while sitting in front of a history
     * that only ever existed in this tab.
     *
     * Any other status is a real failure: those rows survive and would
     * reappear on the next fetch, so the list is left alone and the caller
     * gets to report it rather than showing data loss followed by
     * resurrection.
     */
    const clearHistory = async () => {
        let deleted = 0
        try {
            const res: any = await $fetch('/api/shop/history', { method: 'DELETE' })
            deleted = res?.deleted ?? 0
        } catch (err: any) {
            const status = err?.statusCode ?? err?.response?.status
            if (status !== 401) throw err
        }
        history.value = []
        return deleted
    }

    // ── Playlists ──────────────────────────────────────────────
    const fetchPlaylists = async () => {
        try {
            const res = await $fetch('/api/shop/playlists')
            playlists.value = res?.items || []
        } catch {/* ignore */}
    }
    const createPlaylist = async ({ name, description, trackId, coverPalette }) => {
        const created = await $fetch('/api/shop/playlists', {
            method: 'POST',
            body: { name, description, trackId, coverPalette }
        })
        await fetchPlaylists()
        return created
    }
    const addTrackToPlaylist = async (playlistId, trackId) => {
        const res = await $fetch(`/api/shop/playlists/${encodeURIComponent(playlistId)}/tracks`, {
            method: 'POST',
            body: { trackId }
        })
        await fetchPlaylists()
        return res
    }
    const removeTrackFromPlaylist = async (playlistId, trackId) => {
        const res = await $fetch(
            `/api/shop/playlists/${encodeURIComponent(playlistId)}/tracks/${encodeURIComponent(trackId)}`,
            { method: 'DELETE' }
        )
        await fetchPlaylists()
        return res
    }
    const deletePlaylist = async (playlistId) => {
        const res = await $fetch(`/api/shop/playlists/${encodeURIComponent(playlistId)}`, {
            method: 'DELETE'
        })
        await fetchPlaylists()
        return res
    }
    const renamePlaylist = async (playlistId, { name, description }) => {
        const res = await $fetch(`/api/shop/playlists/${encodeURIComponent(playlistId)}`, {
            method: 'PATCH',
            body: { name, description }
        })
        await fetchPlaylists()
        return res
    }
    /**
     * Flip a playlist between private and unlisted.
     *
     * Returns the updated playlist because the server mints the share slug on
     * the way to `unlisted` (and clears it on the way back) — the caller needs
     * that slug to show the link, and it cannot be predicted client-side.
     */
    const setPlaylistVisibility = async (playlistId, visibility) => {
        const res: any = await $fetch(`/api/shop/playlists/${encodeURIComponent(playlistId)}`, {
            method: 'PATCH',
            body: { visibility }
        })
        await fetchPlaylists()
        return res?.playlist || null
    }
    const reorderPlaylist = async (playlistId, order) => {
        const res = await $fetch(`/api/shop/playlists/${encodeURIComponent(playlistId)}/order`, {
            method: 'PATCH',
            body: { order }
        })
        return res
    }

    // ── Drawer ──────────────────────────────────────────────
    // `origin` is optional `{ x, y }`. When provided, the drawer plays
    // its spawn animation from that point — used by the queue button
    // in MasterPlayer to make the container erupt from the icon.
    const openDrawer = (tab, origin) => {
        if (origin && typeof origin.x === 'number' && typeof origin.y === 'number') {
            drawerOrigin.value = { x: origin.x, y: origin.y }
        }
        if (tab) drawerTab.value = tab
        drawerOpen.value = true
    }
    const closeDrawer = () => { drawerOpen.value = false }
    const toggleDrawer = (tab, origin) => {
        if (drawerOpen.value && (!tab || drawerTab.value === tab)) {
            drawerOpen.value = false
        } else {
            if (origin && typeof origin.x === 'number' && typeof origin.y === 'number') {
                drawerOrigin.value = { x: origin.x, y: origin.y }
            }
            if (tab) drawerTab.value = tab
            drawerOpen.value = true
        }
    }

    return {
        // existing
        currentTrack, isPlaying, currentTime, duration, volume,
        play, pause, toggle, stop, seek,
        // new
        queue, history, playlists,
        drawerOpen, drawerTab, drawerOrigin, historyPulseAt,
        hydrateQueue,
        addToQueue, playNext, removeFromQueue, clearQueue, moveInQueue, advanceQueue,
        logHistory, fetchHistory, clearHistory,
        fetchPlaylists, createPlaylist, addTrackToPlaylist, removeTrackFromPlaylist,
        deletePlaylist, renamePlaylist, reorderPlaylist, setPlaylistVisibility,
        openDrawer, closeDrawer, toggleDrawer
    }
}

// Server history rows store the populated Track + Release; the player
// state shape uses a flatter object. This shim makes the server's row
// look like what `play()` would push into the queue / current track,
// so the drawer's history tab can hand a row straight to `play()`.
function serverHistoryToTrack(row) {
    const t = row?.track || {}
    const r = row?.release || {}
    const art = r?.artwork?.thumbnail?.url || r?.artwork?.original?.url || r?.artwork?.url || null
    return {
        _id: row._id,
        playedAt: row.playedAt,
        source: row.source,
        track: {
            id: String(t._id || ''),
            _id: t._id,
            title: t.name ? `${trackDisplayTitle(t)} — ${t.displayArtist || r.displayArtist || ''}` : 'Unknown track',
            url: t.audio?.mp3?.url || null,
            duration: t.duration || 0,
            previewStart: t.previewStart || 0,
            previewLength: t.previewLength || 30,
            // Where the preview slice sits in the full track. The players need it
            // to place the region on a waveform that still shows the whole song.
            previewOffset: t.previewOffset || 0,
            imageUrl: art ? (art.startsWith('http') ? `/api/assets/serve?url=${encodeURIComponent(art)}` : art) : '',
            audio: { waveform: t.audio?.waveform || [] },
            release: r
        }
    }
}
